/**
 * Doppler Assessment Component
 * User-friendly interface for vascular Doppler measurements
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Grid,
  Divider,
  Chip,
  Card,
  CardContent,
  InputAdornment,
  Tabs,
  Tab,
  FormControlLabel,
  Checkbox,
  Alert,
} from '@mui/material';
import TimelineIcon from '@mui/icons-material/Timeline';
import FavoriteIcon from '@mui/icons-material/Favorite';
import AccountTreeIcon from '@mui/icons-material/AccountTree';

import doppler, {
  calculateAllIndices,
  interpretUA_PI,
  assessUAFlow,
  calculateMCA_MoM,
  interpretMCA_PSV,
  interpretMCA_PI,
  calculateCPR,
  interpretCPR,
  interpretUterinePI,
  getSeverityColor,
} from '../../utils/dopplerCalculations';

const VelocityField = ({ label, value, onChange, unit = 'cm/s' }) => (
  <TextField
    fullWidth
    size="small"
    label={label}
    type="number"
    value={value || ''}
    onChange={(e) => onChange(e.target.value ? parseFloat(e.target.value) : null)}
    InputProps={{
      endAdornment: <InputAdornment position="end">{unit}</InputAdornment>,
      inputProps: { min: 0, step: 0.1 }
    }}
    sx={{ '& .MuiInputBase-input': { textAlign: 'right' } }}
  />
);

const IndexDisplay = ({ label, value, interpretation, unit = '' }) => {
  const color = interpretation ? getSeverityColor(interpretation.severity) : '#9e9e9e';

  return (
    <Box sx={{ textAlign: 'center', p: 1 }}>
      <Typography variant="caption" color="textSecondary">
        {label}
      </Typography>
      <Typography
        variant="h6"
        fontWeight="bold"
        sx={{ color: value ? color : 'text.disabled' }}
      >
        {value !== null ? value.toFixed(2) : '-'}{unit}
      </Typography>
      {interpretation && (
        <Chip
          label={interpretation.status}
          size="small"
          sx={{
            mt: 0.5,
            bgcolor: `${color}20`,
            color: color,
            fontSize: '0.65rem',
            height: 20,
          }}
        />
      )}
    </Box>
  );
};

const VesselPanel = ({ title, icon, children, indices, interpretation }) => (
  <Card elevation={0} sx={{ border: '1px solid #e0e0e0', mb: 2 }}>
    <CardContent>
      <Box display="flex" alignItems="center" gap={1} mb={2}>
        {icon}
        <Typography variant="subtitle1" fontWeight="bold">
          {title}
        </Typography>
        {interpretation && (
          <Chip
            label={interpretation.status}
            size="small"
            sx={{
              ml: 'auto',
              bgcolor: `${getSeverityColor(interpretation.severity)}20`,
              color: getSeverityColor(interpretation.severity),
            }}
          />
        )}
      </Box>
      {children}
      {indices && (
        <Box
          display="flex"
          justifyContent="space-around"
          sx={{ mt: 2, pt: 2, borderTop: '1px solid #f0f0f0' }}
        >
          <IndexDisplay label="RI" value={indices.ri} />
          <IndexDisplay label="PI" value={indices.pi} />
          <IndexDisplay label="S/D" value={indices.sd} />
        </Box>
      )}
    </CardContent>
  </Card>
);

const DopplerAssessment = ({ gaWeeks, onDopplerChange }) => {
  const [activeTab, setActiveTab] = useState(0);

  // Umbilical Artery
  const [ua, setUA] = useState({ psv: null, edv: null });
  const [uaIndices, setUAIndices] = useState(null);
  const [uaInterpretation, setUAInterpretation] = useState(null);

  // Middle Cerebral Artery
  const [mca, setMCA] = useState({ psv: null, edv: null });
  const [mcaIndices, setMCAIndices] = useState(null);
  const [mcaInterpretation, setMCAInterpretation] = useState(null);
  const [mcaMoM, setMCAMoM] = useState(null);

  // Uterine Artery
  const [utA, setUtA] = useState({ left: { pi: null }, right: { pi: null }, hasNotch: false });
  const [utAInterpretation, setUtAInterpretation] = useState(null);

  // CPR
  const [cpr, setCPR] = useState(null);
  const [cprInterpretation, setCPRInterpretation] = useState(null);

  // Calculate UA indices
  useEffect(() => {
    if (ua.psv) {
      const indices = calculateAllIndices(ua.psv, ua.edv);
      setUAIndices(indices);

      const flowAssess = assessUAFlow(ua.edv);
      const piInterpret = interpretUA_PI(indices.pi, gaWeeks);

      setUAInterpretation(
        flowAssess?.severity === 'critical' || flowAssess?.severity === 'abnormal'
          ? flowAssess
          : piInterpret
      );
    } else {
      setUAIndices(null);
      setUAInterpretation(null);
    }
  }, [ua, gaWeeks]);

  // Calculate MCA indices
  useEffect(() => {
    if (mca.psv) {
      const indices = calculateAllIndices(mca.psv, mca.edv);
      setMCAIndices(indices);

      const mom = calculateMCA_MoM(mca.psv, gaWeeks);
      setMCAMoM(mom);

      const psvInterpret = interpretMCA_PSV(mca.psv, gaWeeks);
      const piInterpret = interpretMCA_PI(indices.pi, gaWeeks);

      // Use PSV interpretation for anemia, PI for redistribution
      setMCAInterpretation(
        psvInterpret?.severity === 'abnormal' ? psvInterpret : piInterpret
      );
    } else {
      setMCAIndices(null);
      setMCAInterpretation(null);
      setMCAMoM(null);
    }
  }, [mca, gaWeeks]);

  // Calculate CPR
  useEffect(() => {
    if (mcaIndices?.pi && uaIndices?.pi) {
      const cprValue = calculateCPR(mcaIndices.pi, uaIndices.pi);
      setCPR(cprValue);
      setCPRInterpretation(interpretCPR(cprValue, gaWeeks));
    } else {
      setCPR(null);
      setCPRInterpretation(null);
    }
  }, [mcaIndices, uaIndices, gaWeeks]);

  // Calculate Uterine interpretation
  useEffect(() => {
    const avgPI = (utA.left.pi && utA.right.pi)
      ? (utA.left.pi + utA.right.pi) / 2
      : utA.left.pi || utA.right.pi;

    if (avgPI) {
      setUtAInterpretation(interpretUterinePI(avgPI, gaWeeks, utA.hasNotch));
    } else {
      setUtAInterpretation(null);
    }
  }, [utA, gaWeeks]);

  // Emit changes
  useEffect(() => {
    if (onDopplerChange) {
      onDopplerChange({
        ua: { ...ua, indices: uaIndices, interpretation: uaInterpretation },
        mca: { ...mca, indices: mcaIndices, interpretation: mcaInterpretation, mom: mcaMoM },
        utA: { ...utA, interpretation: utAInterpretation },
        cpr: { value: cpr, interpretation: cprInterpretation },
      });
    }
  }, [ua, uaIndices, uaInterpretation, mca, mcaIndices, mcaInterpretation, mcaMoM, utA, utAInterpretation, cpr, cprInterpretation, onDopplerChange]);

  return (
    <Paper elevation={0} sx={{ p: 2, border: '1px solid #e0e0e0' }}>
      <Typography variant="subtitle2" color="textSecondary" gutterBottom>
        Doppler Assessment {gaWeeks && `(${Math.floor(gaWeeks)}w)`}
      </Typography>

      {/* CPR Summary if available */}
      {cpr && (
        <Alert
          severity={cprInterpretation?.severity === 'normal' ? 'success' : 'warning'}
          sx={{ mb: 2 }}
        >
          <strong>Cerebroplacental Ratio (CPR): {cpr.toFixed(2)}</strong>
          {' - '}{cprInterpretation?.status}
        </Alert>
      )}

      <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ mb: 2 }}>
        <Tab label="Umbilical Artery" />
        <Tab label="MCA" />
        <Tab label="Uterine Artery" />
      </Tabs>

      {/* Umbilical Artery Tab */}
      {activeTab === 0 && (
        <VesselPanel
          title="Umbilical Artery (UA)"
          icon={<AccountTreeIcon color="primary" />}
          indices={uaIndices}
          interpretation={uaInterpretation}
        >
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <VelocityField
                label="PSV"
                value={ua.psv}
                onChange={(v) => setUA(prev => ({ ...prev, psv: v }))}
              />
            </Grid>
            <Grid item xs={6}>
              <VelocityField
                label="EDV"
                value={ua.edv}
                onChange={(v) => setUA(prev => ({ ...prev, edv: v }))}
              />
            </Grid>
          </Grid>
          {ua.edv !== null && ua.edv <= 0 && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {ua.edv < 0 ? 'Reversed End Diastolic Flow (REDF)' : 'Absent End Diastolic Flow (AEDF)'}
              {' - Requires immediate clinical evaluation'}
            </Alert>
          )}
        </VesselPanel>
      )}

      {/* MCA Tab */}
      {activeTab === 1 && (
        <VesselPanel
          title="Middle Cerebral Artery (MCA)"
          icon={<TimelineIcon sx={{ color: '#9c27b0' }} />}
          indices={mcaIndices}
          interpretation={mcaInterpretation}
        >
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <VelocityField
                label="PSV"
                value={mca.psv}
                onChange={(v) => setMCA(prev => ({ ...prev, psv: v }))}
              />
            </Grid>
            <Grid item xs={6}>
              <VelocityField
                label="EDV"
                value={mca.edv}
                onChange={(v) => setMCA(prev => ({ ...prev, edv: v }))}
              />
            </Grid>
          </Grid>
          {mcaMoM && (
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Typography variant="caption" color="textSecondary">
                MCA PSV MoM (Fetal Anemia Screening)
              </Typography>
              <Typography
                variant="h5"
                fontWeight="bold"
                color={mcaMoM >= 1.5 ? 'error.main' : mcaMoM >= 1.29 ? 'warning.main' : 'success.main'}
              >
                {mcaMoM.toFixed(2)} MoM
              </Typography>
              {mcaMoM >= 1.5 && (
                <Alert severity="error" sx={{ mt: 1 }}>
                  Elevated MCA PSV - Risk of moderate/severe fetal anemia
                </Alert>
              )}
            </Box>
          )}
        </VesselPanel>
      )}

      {/* Uterine Artery Tab */}
      {activeTab === 2 && (
        <VesselPanel
          title="Uterine Arteries"
          icon={<FavoriteIcon sx={{ color: '#e91e63' }} />}
          interpretation={utAInterpretation}
        >
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                size="small"
                label="Left UtA PI"
                type="number"
                value={utA.left.pi || ''}
                onChange={(e) => setUtA(prev => ({
                  ...prev,
                  left: { pi: e.target.value ? parseFloat(e.target.value) : null }
                }))}
                InputProps={{ inputProps: { min: 0, step: 0.01 } }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                size="small"
                label="Right UtA PI"
                type="number"
                value={utA.right.pi || ''}
                onChange={(e) => setUtA(prev => ({
                  ...prev,
                  right: { pi: e.target.value ? parseFloat(e.target.value) : null }
                }))}
                InputProps={{ inputProps: { min: 0, step: 0.01 } }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={utA.hasNotch}
                    onChange={(e) => setUtA(prev => ({ ...prev, hasNotch: e.target.checked }))}
                  />
                }
                label="Early diastolic notch present"
              />
            </Grid>
          </Grid>
          {(utA.left.pi || utA.right.pi) && (
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Typography variant="caption" color="textSecondary">
                Mean Uterine Artery PI
              </Typography>
              <Typography variant="h5" fontWeight="bold">
                {((utA.left.pi || 0) + (utA.right.pi || 0)) /
                  ((utA.left.pi ? 1 : 0) + (utA.right.pi ? 1 : 0)) || '-'}
              </Typography>
            </Box>
          )}
        </VesselPanel>
      )}
    </Paper>
  );
};

export default DopplerAssessment;
